/**
 * Apple-Calendar-style Repeat configuration field for the activity Add Session form.
 *
 * Layout (PRD docs/prd-repeated-sessions.md §3.1):
 *   Repeat: [Never ▾]   Until: [After ▾] [N] times
 *                              [On Date ▾] [date picker]
 *
 * When Repeat ≠ Never, a preview row appears below listing the first ~8 occurrence
 * PT dates (with "+N more" tail when truncated).
 *
 * The field is intentionally only rendered for newly-added sessions (no `id`).
 * Edits to an existing series don't change the repeat config — host edits each
 * occurrence individually in the SessionFormList (PRD §5).
 */

/* eslint-disable import/no-named-as-default */
import { useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';

import {
  AFTER_N_MAX,
  AFTER_N_MIN,
  CUSTOM_INTERVAL_MAX,
  CUSTOM_INTERVAL_MIN,
  SERIES_END_ON_DATE_MAX_OFFSET_DAYS,
  expandSeries,
  type CustomConfig,
  type CustomFrequency,
  type EndRepeat,
  type RepeatConfig,
  type RepeatMode,
  type Weekday,
} from '../../utils/repeatedSessions';
import { ptInstantParts } from '../../utils/timezone';

interface RepeatFieldProps {
  anchorStartISO: string;
  anchorEndISO: string;
  value: RepeatConfig;
  onChange: (next: RepeatConfig) => void;
  disabled?: boolean;
}

const REPEAT_OPTIONS: { value: RepeatMode; label: string }[] = [
  { value: 'NEVER', label: 'Never' },
  { value: 'DAILY', label: 'Every Day' },
  { value: 'WEEKLY', label: 'Every Week' },
  { value: 'MONTHLY', label: 'Every Month' },
  { value: 'CUSTOM', label: 'Custom…' },
];

const FREQUENCY_OPTIONS: { value: CustomFrequency; label: string }[] = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
];

const WEEKDAY_LABELS: { idx: Weekday; short: string }[] = [
  { idx: 0, short: 'S' },
  { idx: 1, short: 'M' },
  { idx: 2, short: 'T' },
  { idx: 3, short: 'W' },
  { idx: 4, short: 'T' },
  { idx: 5, short: 'F' },
  { idx: 6, short: 'S' },
];

const PREVIEW_MAX_DATES = 8;

function repeatSummary(config: RepeatConfig): string {
  if (config.mode === 'NEVER') return 'Never';
  if (config.mode === 'DAILY') return 'Every Day';
  if (config.mode === 'WEEKLY') return 'Every Week';
  if (config.mode === 'MONTHLY') return 'Every Month';
  // CUSTOM
  const c = config.custom;
  if (!c) return 'Custom…';
  if (c.frequency === 'DAILY') {
    return `Custom (every ${c.every} day${c.every === 1 ? '' : 's'})`;
  }
  if (c.frequency === 'MONTHLY') {
    return `Custom (every ${c.every} month${c.every === 1 ? '' : 's'})`;
  }
  // WEEKLY
  const dayLabels = (c.weekdays ?? [])
    .slice()
    .sort()
    .map((d) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]);
  return `Custom (every ${c.every} week${c.every === 1 ? '' : 's'} on ${dayLabels.join(', ')})`;
}

function formatPtMonthDay(iso: string): string {
  const p = ptInstantParts(new Date(iso));
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[p.month - 1]} ${p.day}`;
}

function defaultCustomForMode(anchorWeekday: Weekday): CustomConfig {
  // Sensible default: weekly with anchor's weekday selected.
  return { frequency: 'WEEKLY', every: 1, weekdays: [anchorWeekday] };
}

export function RepeatField({
  anchorStartISO,
  anchorEndISO,
  value,
  onChange,
  disabled = false,
}: RepeatFieldProps) {
  const [customModalOpen, setCustomModalOpen] = useState(false);
  // When the user opens the custom modal from a non-CUSTOM mode, remember the previous mode
  // so we can revert if they cancel. Null when modal was opened from an already-CUSTOM state
  // ("Edit custom…") — cancellation in that case leaves the existing config intact.
  const [revertModeOnCancel, setRevertModeOnCancel] = useState<RepeatMode | null>(null);

  // Anchor's PT weekday (for default Custom Weekly selection).
  const anchorWeekday = useMemo(
    () => ptInstantParts(new Date(anchorStartISO)).weekday as Weekday,
    [anchorStartISO]
  );

  const handleModeChange = (mode: RepeatMode) => {
    if (mode === 'CUSTOM') {
      // Open the custom modal. Pre-seed if not already custom, and remember the previous mode
      // so cancel can revert (PRD §3.3 — selecting Custom should not commit until OK).
      if (value.mode !== 'CUSTOM') {
        setRevertModeOnCancel(value.mode);
        onChange({
          mode: 'CUSTOM',
          custom: defaultCustomForMode(anchorWeekday),
          endRepeat: value.endRepeat ?? { mode: 'AFTER', n: 8 },
        });
      } else {
        setRevertModeOnCancel(null);
      }
      setCustomModalOpen(true);
      return;
    }
    setRevertModeOnCancel(null);
    if (mode === 'NEVER') {
      onChange({ mode: 'NEVER' });
      return;
    }
    onChange({
      mode,
      endRepeat: value.endRepeat ?? { mode: 'AFTER', n: 8 },
    });
  };

  const handleEndRepeatChange = (next: EndRepeat) => {
    onChange({ ...value, endRepeat: next });
  };

  const handleCustomCancel = () => {
    setCustomModalOpen(false);
    if (revertModeOnCancel !== null) {
      const previous = revertModeOnCancel;
      setRevertModeOnCancel(null);
      if (previous === 'NEVER') {
        onChange({ mode: 'NEVER' });
      } else if (previous === 'CUSTOM') {
        // Defensive: shouldn't happen because we only set revertModeOnCancel when previous was non-CUSTOM.
      } else {
        onChange({ mode: previous, endRepeat: value.endRepeat ?? { mode: 'AFTER', n: 8 } });
      }
    }
  };

  const handleCustomConfirm = (custom: CustomConfig) => {
    onChange({
      mode: 'CUSTOM',
      custom,
      endRepeat: value.endRepeat ?? { mode: 'AFTER', n: 8 },
    });
    setRevertModeOnCancel(null);
    setCustomModalOpen(false);
  };

  // ── Preview computation ──────────────────────────────────────────────────
  const previewResult = useMemo(() => {
    if (value.mode === 'NEVER') return null;
    return expandSeries({ startTime: anchorStartISO, endTime: anchorEndISO }, value);
  }, [value, anchorStartISO, anchorEndISO]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Repeat:</label>
        <select
          value={value.mode}
          onChange={(e) => handleModeChange(e.target.value as RepeatMode)}
          disabled={disabled}
          className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
        >
          {REPEAT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.value === 'CUSTOM' && value.mode === 'CUSTOM' ? repeatSummary(value) : opt.label}
            </option>
          ))}
        </select>

        {value.mode !== 'NEVER' && (
          <EndRepeatInline
            value={value.endRepeat ?? { mode: 'AFTER', n: 8 }}
            anchorStartISO={anchorStartISO}
            onChange={handleEndRepeatChange}
            disabled={disabled}
          />
        )}

        {value.mode === 'CUSTOM' && (
          <button
            type="button"
            onClick={() => setCustomModalOpen(true)}
            disabled={disabled}
            className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 underline"
          >
            Edit custom…
          </button>
        )}
      </div>

      {previewResult && (
        <div className="text-xs text-gray-600">
          {previewResult.ok ? (
            <PreviewLine occurrences={previewResult.occurrences} />
          ) : (
            <span className="text-red-600">⚠ {previewResult.error}</span>
          )}
        </div>
      )}

      {customModalOpen && (
        <CustomRepeatModal
          initial={
            value.mode === 'CUSTOM' && value.custom
              ? value.custom
              : defaultCustomForMode(anchorWeekday)
          }
          anchorWeekday={anchorWeekday}
          onCancel={handleCustomCancel}
          onConfirm={handleCustomConfirm}
        />
      )}
    </div>
  );
}

// ─── End Repeat (After / On Date toggle, inline) ─────────────────────────────

interface EndRepeatInlineProps {
  value: EndRepeat;
  anchorStartISO: string;
  onChange: (next: EndRepeat) => void;
  disabled: boolean;
}

function EndRepeatInline({ value, anchorStartISO, onChange, disabled }: EndRepeatInlineProps) {
  // The On Date picker operates on PT calendar dates regardless of the browser timezone:
  // we build min/max/default JS Date objects out of the anchor's PT YMD, then treat the
  // picker's local Y/M/D as PT Y/M/D on read. This keeps the picker DST-safe and avoids
  // off-by-one mismatches between browser local and PT (PRD §4.2 / §3.1 On Date semantics).
  const anchorPtYmd = useMemo(() => {
    const p = ptInstantParts(new Date(anchorStartISO));
    return { year: p.year, month: p.month, day: p.day };
  }, [anchorStartISO]);

  const minOnDate = useMemo(
    () => new Date(anchorPtYmd.year, anchorPtYmd.month - 1, anchorPtYmd.day + 1, 12, 0, 0),
    [anchorPtYmd]
  );
  const maxOnDate = useMemo(
    () =>
      new Date(
        anchorPtYmd.year,
        anchorPtYmd.month - 1,
        anchorPtYmd.day + SERIES_END_ON_DATE_MAX_OFFSET_DAYS,
        12,
        0,
        0
      ),
    [anchorPtYmd]
  );
  const defaultOnDate = useMemo(
    () => new Date(anchorPtYmd.year, anchorPtYmd.month - 1, anchorPtYmd.day + 56, 12, 0, 0),
    [anchorPtYmd]
  );

  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-gray-500">Until:</span>
      <select
        value={value.mode}
        onChange={(e) => {
          const next = e.target.value as 'AFTER' | 'ON_DATE';
          if (next === 'AFTER') onChange({ mode: 'AFTER', n: 8 });
          else {
            const candidate = defaultOnDate > maxOnDate ? maxOnDate : defaultOnDate;
            onChange({ mode: 'ON_DATE', dateISO: toYmd(candidate) });
          }
        }}
        disabled={disabled}
        className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
      >
        <option value="AFTER">After</option>
        <option value="ON_DATE">On Date</option>
      </select>

      {value.mode === 'AFTER' ? (
        <>
          <input
            type="number"
            min={AFTER_N_MIN}
            max={AFTER_N_MAX}
            value={value.n}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) onChange({ mode: 'AFTER', n });
            }}
            disabled={disabled}
            className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-sm text-gray-500">times</span>
        </>
      ) : (
        <DatePicker
          selected={value.dateISO ? new Date(`${value.dateISO}T00:00:00`) : null}
          onChange={(date: Date | null) => {
            if (date) onChange({ mode: 'ON_DATE', dateISO: toYmd(date) });
          }}
          minDate={minOnDate}
          maxDate={maxOnDate}
          dateFormat="MMM d, yyyy"
          disabled={disabled}
          className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
        />
      )}
    </span>
  );
}

function toYmd(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ─── Preview ─────────────────────────────────────────────────────────────────

function PreviewLine({ occurrences }: { occurrences: { startTime: string; endTime: string }[] }) {
  const shown = occurrences.slice(0, PREVIEW_MAX_DATES);
  const remainder = occurrences.length - shown.length;
  return (
    <span>
      Will create {occurrences.length} session{occurrences.length === 1 ? '' : 's'}:{' '}
      {shown.map((o, i) => (
        <span key={o.startTime}>
          {i > 0 && ' · '}
          {formatPtMonthDay(o.startTime)}
        </span>
      ))}
      {remainder > 0 && <span className="text-gray-400"> · +{remainder} more</span>}
    </span>
  );
}

// ─── Custom Modal (Frequency + Every N + weekday picker) ─────────────────────

interface CustomRepeatModalProps {
  initial: CustomConfig;
  anchorWeekday: Weekday;
  onCancel: () => void;
  onConfirm: (custom: CustomConfig) => void;
}

function CustomRepeatModal({
  initial,
  anchorWeekday,
  onCancel,
  onConfirm,
}: CustomRepeatModalProps) {
  const [frequency, setFrequency] = useState<CustomFrequency>(initial.frequency);
  const [every, setEvery] = useState<number>(initial.every);
  const [weekdays, setWeekdays] = useState<Set<Weekday>>(() => {
    const init = initial.weekdays ?? [anchorWeekday];
    return new Set(init);
  });

  // Esc closes the modal.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel]);

  const toggleWeekday = (idx: Weekday) => {
    setWeekdays((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleOk = () => {
    const config: CustomConfig = {
      frequency,
      every,
      ...(frequency === 'WEEKLY' ? { weekdays: Array.from(weekdays).sort() as Weekday[] } : {}),
    };
    onConfirm(config);
  };

  const okDisabled =
    !Number.isInteger(every) ||
    every < CUSTOM_INTERVAL_MIN ||
    every > CUSTOM_INTERVAL_MAX ||
    (frequency === 'WEEKLY' && weekdays.size === 0);

  const overlayRef = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={overlayRef}
      onClick={(e) => {
        if (e.target === overlayRef.current) onCancel();
      }}
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
    >
      <div className="bg-white rounded-lg shadow-xl w-80 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Custom Repeat</h3>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-700 w-20">Frequency:</label>
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as CustomFrequency)}
            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          >
            {FREQUENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-700">
          <span>Every</span>
          <input
            type="number"
            min={CUSTOM_INTERVAL_MIN}
            max={CUSTOM_INTERVAL_MAX}
            value={every}
            onChange={(e) => setEvery(Number(e.target.value))}
            className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span>
            {frequency === 'DAILY' ? `day${every === 1 ? '' : 's'}` : ''}
            {frequency === 'WEEKLY' ? `week${every === 1 ? '' : 's'}${' on:'}` : ''}
            {frequency === 'MONTHLY' ? `month${every === 1 ? '' : 's'}` : ''}
          </span>
        </div>

        {frequency === 'WEEKLY' && (
          <div className="flex justify-center gap-1">
            {WEEKDAY_LABELS.map(({ idx, short }) => {
              const active = weekdays.has(idx);
              return (
                <button
                  type="button"
                  key={idx}
                  onClick={() => toggleWeekday(idx)}
                  aria-pressed={active}
                  className={`w-8 h-8 rounded-md text-sm font-medium border transition-colors ${
                    active
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {short}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleOk}
            disabled={okDisabled}
            className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
